import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail } from "lucide-react";
import { Confetti } from "@/components/illustrations/Confetti";
import { registrationEnabled } from "@/config/features";

export function Footer() {
  const { t } = useTranslation("common");
  const year = new Date().getFullYear();
  return (
    <footer className="relative overflow-hidden bg-surface-deep text-surface-deep-foreground">
      {/* Confetti decoration on the left edge — a quiet poster nod at the
          page's foot. Low density / no animation so it never competes with
          the dashboard or marketing copy above. */}
      <Confetti
        className="absolute -left-12 top-0 hidden h-full w-72 opacity-50 md:block"
        density="low"
      />

      <div className="container relative py-12 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-2">
          {/* Logo lockup sits on a white panel so the navy/teal artwork stays
              legible against the dark navy footer surface. */}
          <div className="inline-flex rounded-xl bg-white p-4">
            <img
              src="/BBCxTEKLRR2026_Logo-PNG.png"
              alt="Bursa Bull Charge × The Edge KL Rat Race 2026"
              className="h-12 w-auto md:h-16"
            />
          </div>
          <p className="mt-4 text-sm text-surface-deep-foreground/70 max-w-md">
            {t("footer.descriptionLine1")}
            <br />
            {t("footer.descriptionLine2")}
          </p>
        </div>

        <div>
          <h2 className="text-xs uppercase tracking-[0.25em] text-surface-deep-foreground/60 mb-3">{t("footer.quickLinks")}</h2>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-primary">{t("nav.home")}</Link></li>
            <li><Link to="/race-details" className="hover:text-primary">{t("nav.raceDay")}</Link></li>
            {registrationEnabled && (
              <li><Link to="/registration" className="hover:text-primary">{t("nav.registration")}</Link></li>
            )}
            <li><Link to="/sponsorship" className="hover:text-primary">{t("nav.sponsorship")}</Link></li>
            <li><Link to="/sponsors" className="hover:text-primary">{t("nav.sponsors")}</Link></li>
            <li><Link to="/contact" className="hover:text-primary">{t("nav.contact")}</Link></li>
            <li><Link to="/accessibility" className="hover:text-primary">{t("nav.accessibility")}</Link></li>
          </ul>
        </div>

        <div>
          <h2 className="text-xs uppercase tracking-[0.25em] text-surface-deep-foreground/60 mb-3">{t("footer.contact")}</h2>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              <a href="mailto:BBCKLRR@bizedge.com" className="hover:text-primary break-all">
                BBCKLRR@bizedge.com
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="relative border-t border-surface-deep-foreground/10">
        <div className="container py-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between text-xs text-surface-deep-foreground/60">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{t("footer.copyright", { year })}</span>
            <span aria-hidden className="hidden sm:inline text-surface-deep-foreground/30">·</span>
            {/*
              * Agency credit — AIKI Technologies built the microsite for the
              * client (Bursa Malaysia × The Edge). Kept deliberately quiet so
              * it never competes with the client brand: same muted size and
              * tone as the copyright, with only the agency name picking up a
              * hover tint.
              */}
            <span>
              {t("footer.builtBy")}{" "}
              <a
                href="https://aikitech.com.my"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-surface-deep-foreground/80 underline-offset-4 hover:text-primary hover:underline"
              >
                AIKI Technologies
              </a>
            </span>
          </p>
          {/* Tagline aligned with the 2026 key visual: "Run As One. Run For All." */}
          <p className="uppercase tracking-[0.25em]">
            Run As One <span className="text-primary">·</span> Run For All
          </p>
        </div>
      </div>
    </footer>
  );
}
